import React, { useRef } from 'react'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { Button } from 'react-bootstrap'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

import styles from './style.module.scss'
import swapIcon from '@assets/images/icon/swap-icon.svg'
import teamPlaceholder from '@assets/images/placeholder/team-placeholder.jpg'
import useModal from '@shared/hooks/useModal'
import { allRoutes } from '@shared/constants/allRoutes'
import { getImgURL } from '@shared/utils'
import { pageLoading } from '@shared/libs/allLoader'
import SearchIcon from '@assets/images/icon/search-icon.svg'

const MyImage = dynamic(() => import('@shared/components/myImage'))
const PlayerImg = dynamic(() => import('@shared/components/playerImg'))
const CompareList = dynamic(() => import('@shared/components/player/compareList'), { loading: () => pageLoading() })
const CommonModal = dynamic(() => import('@shared-components/commonModal'))
const PlayerCompareWidget = dynamic(() => import('@shared-components/player/playerCompare'))

const ComparePlayerInfo = ({ firstPlayer, secondPlayer, playerInfo, isSmall, onChangePlayer, children }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const selectedPlayer = useRef()
  const { toggle, close, isShowing } = useModal()

  function handlePlayerSelect(p) {
    if (onChangePlayer) {
      onChangePlayer(p)
    } else {
      const slug = (p?.oSeo?.sSlug || '').replace('cricket-players/', '')
      const p1Slug = (firstPlayer?.oSeo?.sSlug || '').replace('cricket-players/', '')
      const p2Slug = (secondPlayer?.oSeo?.sSlug || '').replace('cricket-players/', '')
      const params = selectedPlayer.current === 1 ? [slug, p2Slug] : [p1Slug, slug]
      router.push(router.pathname, allRoutes.comparePlayer(...params), { shallow: true })

      const event = new CustomEvent('changePlayer', {
        bubbles: true,
        detail: { willChange: selectedPlayer.current, player: p }
      })
      document.dispatchEvent(event)
    }
    close()
  }

  return (
    <>
      <div
        className={`${styles.playerInfo} d-flex flex-wrap flex-md-nowrap align-items-center common-box p-0 overflow-hidden position-relative`}
      >
        <div className={`${styles.player} position-relative flex-shrink-0`}>
          {firstPlayer?._id && (
            <div className={`${styles.info} ${isSmall ? 'p-2 p-sm-3 pb-0 pb-sm-0' : 'p-2 p-lg-3 pb-1 pb-lg-1'} pb-0`}>
              <h4 className="mb-01 mb-md-0 d-flex align-items-center text-nowrap text-uppercase t-ellipsis overflow-hidden">
                <span className="text-nowrap t-ellipsis overflow-hidden">{firstPlayer?.sFullName || firstPlayer?.sFirstName || '--'}</span>
                <Button
                  variant="link"
                  onClick={() => {
                    selectedPlayer.current = 1
                    toggle()
                  }}
                  className={`${styles.swap} ms-1 flex-shrink-0 ${firstPlayer?._id ? '' : styles.search}`}
                >
                  <MyImage src={swapIcon} width="24" height="24" alt="swap" />
                </Button>
              </h4>
              <h4 className={`${styles.team} mb-1 small-head font-medium text-secondary d-flex align-items-center`}>
                <div className={`${styles.playerTeam} me-1 me-md-2 rounded-circle overflow-hidden`}>
                  <MyImage src={getImgURL(firstPlayer?.oPrimaryTeam?.oImg?.sUrl) || teamPlaceholder} width="24" height="24" alt="team" />
                </div>
                {firstPlayer?.sCountryFull || firstPlayer?.sNationality || '--'}
              </h4>
            </div>
          )}
          <div className={`${styles.playerPic} position-relative`}>
            <PlayerImg head={firstPlayer?.oImg} jersey={firstPlayer?.oPrimaryTeam?.oJersey} size={180} />
            {!firstPlayer?._id && (
              <Button
                onClick={() => {
                  selectedPlayer.current = 1
                  toggle()
                }}
                className="theme-btn small-btn position-absolute bottom-0 start-0 end-0"
              >
                Add Player
              </Button>
            )}
          </div>
        </div>
        <CompareList isSmall data={playerInfo} showTitle={!(firstPlayer?._id || secondPlayer?._id)} />
        <div className={`${styles.player} position-relative flex-shrink-0 text-end ms-auto`}>
          {secondPlayer?._id && (
            <div className={`${styles.info} ${isSmall ? 'p-2 p-sm-3 pb-0 pb-sm-0' : 'p-2 p-lg-3 pb-1 pb-lg-1'} pb-0`}>
              <h4 className="mb-01 mb-md-0 text-uppercase d-flex align-items-center justify-content-end">
                <Button
                  variant="link"
                  onClick={() => {
                    selectedPlayer.current = 2
                    toggle()
                  }}
                  className={`${styles.swap} me-1 flex-shrink-0 ${secondPlayer?._id ? '' : styles.search}`}
                >
                  <MyImage src={secondPlayer?._id ? swapIcon : SearchIcon} width="24" height="24" alt="swap" />
                </Button>
                <span className="text-nowrap t-ellipsis overflow-hidden">
                  {secondPlayer?.sFullName || secondPlayer?.sFirstName || '--'}
                </span>
              </h4>
              <h4 className={`${styles.team} mb-1 small-head font-medium text-secondary d-flex flex-row-reverse align-items-center`}>
                <div className={`${styles.playerTeam} ms-1 ms-md-2 rounded-circle overflow-hidden`}>
                  <MyImage src={getImgURL(secondPlayer?.oPrimaryTeam?.oImg?.sUrl) || teamPlaceholder} width="24" height="24" alt="team" />
                </div>
                {secondPlayer?.sCountryFull || secondPlayer?.sNationality || '--'}
              </h4>
            </div>
          )}
          <div className={`${styles.playerPic} position-relative ms-auto`}>
            <PlayerImg head={secondPlayer?.oImg} jersey={secondPlayer?.oPrimaryTeam?.oJersey} size={180} />
            {!secondPlayer?._id && (
              <Button
                onClick={() => {
                  selectedPlayer.current = 2
                  toggle()
                }}
                className="theme-btn small-btn position-absolute bottom-0 start-0 end-0"
              >
                Add Player
              </Button>
            )}
          </div>
        </div>
      </div>
      {isShowing && (
        <CommonModal title={t('common:CompareBtn')} isConfirm={isShowing} cancel={{ event: toggle }}>
          <PlayerCompareWidget
            playerId={selectedPlayer.current === 1 ? secondPlayer?._id || firstPlayer?._id : firstPlayer?._id || secondPlayer?._id}
            handleSelect={handlePlayerSelect}
          />
        </CommonModal>
      )}
      {typeof children === 'function' ? children({ handlePlayerSelect, selectedPlayer }) : children}
    </>
  )
}

ComparePlayerInfo.propTypes = {
  firstPlayer: PropTypes.object,
  secondPlayer: PropTypes.object,
  playerInfo: PropTypes.array,
  onChangePlayer: PropTypes.func,
  isSmall: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node])
}

export default ComparePlayerInfo
